import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import { i18n, type MessageKey } from 'shared/i18n';

interface RowProps {
  label: ReactNode;
  value: ReactNode;
}

const Row = ({ label, value }: RowProps) => (
  <li className="roll-forward__review-row">
    <span className="roll-forward__review-label">{label}</span>
    <span className="roll-forward__review-value">{value}</span>
  </li>
);

interface CloneItemProps {
  labelKey: MessageKey;
  type?: 'success' | 'danger';
}

// Mirrors the legacy CloneDocumentModal checklist (reuses its i18n keys).
const CloneItem = ({ labelKey, type = 'success' }: CloneItemProps) => (
  <li className={`roll-forward__clone-item ${type === 'success' ? '' : 'text-gray-500'}`}>
    <i className={`fa-regular mr-2 ${type === 'success' ? 'fa-check text-success' : 'fa-xmark'}`} /> {i18n(labelKey)}
  </li>
);

interface StepReviewProps {
  preview: { tagCount: number };
  periodOverrides: Record<string, unknown>;
  keepDismissals: boolean;
}

const StepReview = ({ preview, periodOverrides, keepDismissals }: StepReviewProps) => {
  const { t } = useTranslation(['project-details', 'roll-forward']);

  const editedCount = Object.keys(periodOverrides).length;
  return (
    <div className="roll-forward__step">
      <h4 className="roll-forward__heading">{t('roll-forward:step.review')}</h4>
      <ul className="roll-forward__review">
        <Row label={t('roll-forward:review.tags')} value={`${preview.tagCount} → ${t('roll-forward:review.need-attention')}`} />
        <Row label={t('roll-forward:review.validation')} value={t('roll-forward:review.unvalidated')} />
        <Row
          label={t('roll-forward:review.periods')}
          value={editedCount > 0 ? t('roll-forward:review.periods-edited').replace('{count}', String(editedCount)) : t('roll-forward:review.periods-unchanged')}
        />
        <Row
          label={t('roll-forward:review.dismissals')}
          value={keepDismissals ? t('roll-forward:review.dismissals-kept') : t('roll-forward:review.dismissals-dropped')}
        />
      </ul>

      <p className="roll-forward__clone-info">{t('project-details:clone.modal.info')}</p>
      <div className="row roll-forward__clone-checklist">
        <div className="col-sm-6">
          <ul className="roll-forward__clone-list">
            <CloneItem labelKey="project-details:clone.sections" />
            <CloneItem labelKey="project-details:clone.content" />
            <CloneItem labelKey="project-details:clone.data" />
            <CloneItem labelKey="project-details:clone.templates" />
          </ul>
        </div>
        <div className="col-sm-6">
          <ul className="roll-forward__clone-list">
            <CloneItem labelKey="project-details:clone.users" type="danger" />
            <CloneItem labelKey="project-details:clone.versions" type="danger" />
            <CloneItem labelKey="project-details:clone.comments" type="danger" />
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StepReview;
